import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "./auth.service";
import { AuthUtils } from "./auth.utils";
import { Observable, catchError, throwError } from "rxjs";
import { Router } from "@angular/router";
import { toast } from "ngx-sonner";

type ApiErrorResponse = {
  state?: number;
  message?: string;
  data?: unknown;
};

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  let newReq = req.clone();
  if (
    authService.accessToken &&
    !AuthUtils.isTokenExpired(authService.accessToken)
  ) {
    newReq = req.clone({
      headers: req.headers.set(
        "Authorization",
        "Bearer " + authService.accessToken
      ),
    });
  }

  // Response
  return next(newReq).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          authService.clearSession();
          router.navigate(["/login"], { replaceUrl: true });
        }

        if (error.status === 403) {
          toast.error(getApiErrorMessage(error, "You do not have permission to perform this action."));
        }

        if (error.status >= 500 || error.status === 0) {
          toast.error(getApiErrorMessage(error, "An unexpected error occurred. Please try again."));
        }
      }

      return throwError(() => error);
    })
  );
};

const getApiErrorMessage = (
  error: HttpErrorResponse,
  fallbackMessage: string,
): string => {
  const responseError = error.error;

  if (isApiErrorResponse(responseError) && responseError.message?.trim()) {
    return responseError.message;
  }

  if (typeof responseError === "string" && responseError.trim()) {
    return responseError;
  }

  if (error.message?.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const isApiErrorResponse = (value: unknown): value is ApiErrorResponse => {
  return typeof value === "object" && value !== null && "message" in value;
};
